import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import type { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { UntypedFormBuilder } from '@angular/forms';
import { Subject } from 'rxjs';
import type { SelectedFileInterface } from '@sendpulse/file-manager';

import { CommonModule } from '@angular/common';
import { SpByteFormat } from '@common/pipes/byte-format.pipe';

@Component({
    selector: 'sp-file-preview',
    templateUrl: './sp-file-preview.component.html',
    styleUrls: ['./sp-file-preview.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, SpByteFormat],
})
export class SpFilePreviewComponent implements OnInit, OnChanges {
    private readonly fb = inject(UntypedFormBuilder);

    @Input() public files: SelectedFileInterface[] = [];
    @Input() public showSetupBtn: boolean = true;

    @Output() public setFileEvent: EventEmitter<SelectedFileInterface> = new EventEmitter<SelectedFileInterface>();
    @Output() public updateFilesEvent: EventEmitter<SelectedFileInterface[]> = new EventEmitter<
        SelectedFileInterface[]
    >();
    @Output() public replaceFileEvent: EventEmitter<number> = new EventEmitter<number>();
    @Output() public activateFileEvent: EventEmitter<number> = new EventEmitter<number>();

    public form: UntypedFormGroup;
    public readonly closeSubject: Subject<void> = new Subject<void>();

    constructor() {
        this.initForm();
    }

    public ngOnInit(): void {
        this.setFilesData();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (!changes || !changes.files) {
            return;
        }

        const { files } = changes;
        if (files.previousValue && files.currentValue && files.previousValue.length < files.currentValue.length) {
            const diff: number = files.currentValue.length - files.previousValue.length;
            [...Array(diff).keys()].forEach(() => {
                this.filesForm.push(this.fb.control(null));
            });
        }

        this.filesForm.patchValue(changes.files.currentValue);
    }

    private initForm(): void {
        this.form = this.fb.group({
            files: this.fb.array([]),
        });
    }

    private setFilesData(): void {
        this.files.forEach((file: SelectedFileInterface) => {
            this.filesForm.push(this.fb.control(file));
        });
    }

    public get filesForm(): UntypedFormArray {
        return this.form.get('files') as UntypedFormArray;
    }

    public emitHiddenEventToChild(): void {
        this.closeSubject.next();
    }

    public removeMultipleFile(file: SelectedFileInterface): void {
        const files = this.files.filter((f: SelectedFileInterface) => f !== file);
        this.updateFilesEvent.emit(files.length ? files : null);
    }

    public saveSetupData(setupImageData: SelectedFileInterface, i: number): void {
        this.filesForm.controls[i].patchValue({ ...this.files[i], ...setupImageData });
        this.updateFilesEvent.emit(this.filesForm.value.length ? this.filesForm.value : null);
    }
}
